import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ForgotPasswordEmailInputStep from './ForgotPasswordEmailInputStep';
import { VIEWS_MAP } from '../../../../../router/constants';
import ForgotPasswordMFMethodStep from './ForgotPasswordMFMethodStep';
import ForgotPasswordCodeVerificationStep from './ForgotPasswordCodeVerificationStep';
import ForgotPasswordSSNInputStep from './ForgotPasswordSSNInputStep';
import ForgotPasswordNewPasswordStep from './ForgotPasswordNewPasswordStep';
import ForgotPasswordSuccess from './ForgotPasswordSuccess';
import { useCountdownTimer } from '../../../../../hooks/utilities/useCountdownTimer';
import { usePasswordRecoveryStart } from '../ForgotEmail/hooks/forgotPasswordHooks';
import { ForgotPasswordGeneralErrorModal } from '../Errors/ForgotPasswordGeneralErrorModal';
import { FORGOT_PASSWORD_FLOW } from '../constants';
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/svg/stamp-icons/ic-stamp-timewatch.svg';
import { SERVER_ERRORS_DATA } from '../../../../../constants/serverErrors';

export enum ForgotPasswordFlowSteps {
    EMail = 0,
    MFAMethod = 1,
    CodeVerification = 2,
    SSN = 3,
    NewPassword = 4,
    Success = 5,
    GenericError = 6,
    UserLockedError = 7
}

export const ForgotPasswordFlow = () => {
  const intervalDuration = 30;
  const [emailInput, setEmailInput] = useState<string>('');
  const mfaType = useRef<string>('SMS');
  const [customerId, setCustomerId] = useState<string>('');
  const [challenge, setChallenge] = useState<any>({});
  const [token, setToken] = useState<string>('');
  const [activeComponent, setActiveComponent] = useState<ForgotPasswordFlowSteps>(ForgotPasswordFlowSteps.EMail);
  const navigate = useNavigate();
  const [didResendCode, setDidResendCode] = useState<boolean>(false);
  const resendTimestamp = useRef<any>(null);

  const { t } = useTranslation();

  const { mutateAsync: mutatePasswordRecoveryStart, isLoading: isStartingChallenge } = usePasswordRecoveryStart(
    customerId,
    (code) => {
      switch (code) {
        case SERVER_ERRORS_DATA.USER_LOCKED_CODE:
          setActiveComponent(ForgotPasswordFlowSteps.UserLockedError);
          break;
        default:
          setActiveComponent(ForgotPasswordFlowSteps.GenericError);
      }
    },
  );
  const resendCode = () => {
    mutatePasswordRecoveryStart({ loginName: emailInput, mfaType: mfaType.current })
      .then((response1) => {
        resendTimestamp.current = Date.now();
        setActiveComponent(ForgotPasswordFlowSteps.CodeVerification);
        setChallenge(response1);
      });
  };

  const { startTimer: startTimer2, timeLeft: timeLeft2 } = useCountdownTimer({
    intervalDuration,
    onFinishCountdown: () => {
      resendCode();
    },
  });

  const onCloseFlow = () => {
    navigate(`/${VIEWS_MAP.LOGIN}`);
  };

  switch (activeComponent) {
    case ForgotPasswordFlowSteps.EMail:

      return (
        <ForgotPasswordEmailInputStep
          initialInput={emailInput}
          onClose={onCloseFlow}
          onBack={() => navigate(`/${VIEWS_MAP.RECOVERY}`)}
          onSubmit={(email) => {
            setEmailInput(email);
            setActiveComponent(ForgotPasswordFlowSteps.SSN);
          }}
        />
      );

    case ForgotPasswordFlowSteps.MFAMethod:

      return (
        <ForgotPasswordMFMethodStep
          onClose={onCloseFlow}
          onBack={() => setActiveComponent(ForgotPasswordFlowSteps.SSN)}
          isLoading={isStartingChallenge}
          onOptionSelected={(option) => {
            mfaType.current = option;
            if (resendTimestamp.current) {
              const elapsedTimeInSeconds = (Date.now() - resendTimestamp.current) / 1000;
              if (elapsedTimeInSeconds < intervalDuration) {
                startTimer2(resendTimestamp.current);
                setActiveComponent(ForgotPasswordFlowSteps.CodeVerification);
              } else {
                resendCode();
              }
            } else {
              resendCode();
            }
          }}
        />
      );
    case ForgotPasswordFlowSteps.CodeVerification:
      return (
        <ForgotPasswordCodeVerificationStep
          timeLeftToResend={timeLeft2}
          challenge={challenge}
          isLoading={isStartingChallenge}
          mfaType={mfaType.current}
          didResendCode={didResendCode}
          onClose={onCloseFlow}
          onBack={() => setActiveComponent(ForgotPasswordFlowSteps.MFAMethod)}
          onResend={() => {
            setDidResendCode(true);
            if (!resendTimestamp.current) {
              startTimer2();
            }
            setActiveComponent(ForgotPasswordFlowSteps.MFAMethod);
          }}
          onCodeVerified={(newToken) => {
            setToken(newToken);
            setActiveComponent(ForgotPasswordFlowSteps.NewPassword);
          }}
        />
      );
    case ForgotPasswordFlowSteps.SSN:

      return (
        <ForgotPasswordSSNInputStep
          email={emailInput}
          onSSNVerified={(result) => {
            setCustomerId(result);
            setActiveComponent(ForgotPasswordFlowSteps.MFAMethod);
          }}
          onClose={onCloseFlow}
          onBack={() => setActiveComponent(ForgotPasswordFlowSteps.EMail)}
        />
      );
    case ForgotPasswordFlowSteps.NewPassword:
      return (
        <ForgotPasswordNewPasswordStep
          token={token}
          customerId={challenge.customerId}
          onClose={onCloseFlow}
          onBack={() => {
            navigate(`/${VIEWS_MAP.RECOVERY}`);
          }}
          onPasswordChanged={() => setActiveComponent(ForgotPasswordFlowSteps.Success)}
          onTryAgainClick={() => setActiveComponent(ForgotPasswordFlowSteps.EMail)}
        />
      );
    case ForgotPasswordFlowSteps.Success:
      return (
        <ForgotPasswordSuccess />
      );
    case ForgotPasswordFlowSteps.GenericError:
      return (
        <ForgotPasswordGeneralErrorModal
          title={t(FORGOT_PASSWORD_FLOW.ERRORS.GENERAL_ERROR_TITLE)}
          description={t(FORGOT_PASSWORD_FLOW.ERRORS.GENERAL_ERROR_DESCRIPTION)}
          onAction={() => {
            resendCode();
            setActiveComponent(ForgotPasswordFlowSteps.MFAMethod);
          }}
          onClose={() => { setActiveComponent(ForgotPasswordFlowSteps.MFAMethod); }}
        />
      );
    case ForgotPasswordFlowSteps.UserLockedError:
      return (
        <ForgotPasswordGeneralErrorModal
          title={t(FORGOT_PASSWORD_FLOW.ERRORS.USER_LOCKED_ERROR_TITLE)}
          description={t(FORGOT_PASSWORD_FLOW.ERRORS.USER_LOCKED_ERROR_DESCRIPTION)}
          mainButtonTitle={t(FORGOT_PASSWORD_FLOW.ERRORS.ERROR_BUTTON_GOT_IT)}
          onAction={() => {
            setActiveComponent(ForgotPasswordFlowSteps.MFAMethod);
          }}
          hideSecondaryButton
          icon={<ClockIcon />}
          onClose={() => setActiveComponent(ForgotPasswordFlowSteps.MFAMethod)}
        />
      );
    default:
      return (<div />);
  }
};
